import * as ko from "knockout";
import * as Constants from "../common/constants";
import * as Global from "../common/global";
import * as UI from "../common/ui";
import * as CustomerData from "../data/customerData";
import { IController } from "../interfaces/controller";
import * as Data from "../interfaces/data";

interface ICustomerAddViewModel {
    CustomerName: KnockoutObservable<string>;
    CustomerShortName: KnockoutObservable<string>;
    UserName: KnockoutObservable<string>;
    UserFirstName: KnockoutObservable<string>;
    UserLastName: KnockoutObservable<string>;
    UserEmail: KnockoutObservable<string>;

    TemporaryPassword: KnockoutObservable<string>;
    IsCustomerCreated: KnockoutObservable<boolean>;

    Errors?: KnockoutValidationErrors;
    OnAddCustomer: () => void;
    OnClose: () => void;
    OnCopyPassword: () => void;
}

export class CustomerAddController implements IController {
    // references to DOM elements
    private container: JQuery;
    private viewModel: ICustomerAddViewModel;
    private fieldsOnLoad: any = {};

    constructor() {
        // const me: CustomerAddController = this;

        this.viewModel = {
            // Data input fields
            CustomerName: ko.observable<string>().extend({ required: true }),
            CustomerShortName: ko.observable<string>().extend({ required: true }),
            UserName: ko.observable<string>().extend({ required: true }),
            UserFirstName: ko.observable<string>().extend({ required: true }),
            UserLastName: ko.observable<string>().extend({ required: true }),
            UserEmail: ko.observable<string>().extend({ required: true }),

            // Processing fields
            IsCustomerCreated: ko.observable<boolean>(false),
            TemporaryPassword: ko.observable<string>(),

            // Events
            OnAddCustomer: () => this.addCustomer(),
            OnClose: () => {
                const app = Global.app();
                app.pageManager().closeCurrentPage();
            },
            OnCopyPassword: () => {
                const passwordText = this.container.find("[data-id='password']")[0];
                const range = document.createRange();
                range.selectNode(passwordText);
                window.getSelection().addRange(range);
                try {
                  // Now that we've selected the anchor text, execute the copy command
                  const successful = document.execCommand("copy");
                  if (successful) {
                      UI.showNotificationMessage("Password copied to clipboard.", UI.messageTypeValues.success);
                  } else {
                    UI.showNotificationMessage("Password could not be copied to clipboard.", UI.messageTypeValues.error);
                  }
                } catch (err) {
                    UI.showNotificationMessage("Password could not be copied to clipboard.", UI.messageTypeValues.error);
                }

                // Remove the selections - NOTE: Should use
                // removeRange(range) when it is supported
                window.getSelection().removeAllRanges();
            },
        };

        this.viewModel.Errors = ko.validation.group(this.viewModel, { deep: true });
    }

    public render(container: JQuery): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.container = container;

        ko.applyBindings(this.viewModel, container[0]);

        deferred.resolve();
        return deferred.promise();
    }

    public load(params: any[]): JQueryPromise<any> {
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        this.setDefaultValues();
        this.fieldsOnLoad = JSON.stringify(this.getCustomerAddRequest());

        deferred.resolve();
        return deferred.promise();
    }

    public haveFieldsChanged(): boolean {
        const fieldValues: string = JSON.stringify(this.getCustomerAddRequest());
        return fieldValues !== this.fieldsOnLoad;
    }

    public appRouterID(params: any[]): string {
        return "CustomerAdd";
    }

    // returns a title for the page
    public title(): string {
        return "Add Customer";
    }

    public subTitle(): string {
        return "";
    }

    private isValid(): boolean {
        if (!this.viewModel.Errors) {
            return true;
        }

        if (this.viewModel.Errors().length > 0) {
            this.viewModel.Errors.showAllMessages();
            UI.showErrorMessage([Constants.PHRASE_INVALIDFIELDSONSAVE]);
            return false;
        }

        return true;
    }

    private addCustomer(): JQueryPromise<any> {
        const me: CustomerAddController = this;
        let waitContainer: JQuery;
        const deferred: JQuery.Deferred<any, any, any> = $.Deferred();

        if (this.isValid()) {
            waitContainer = UI.showWaitIndicator(me.container);
            CustomerData.AddCustomer(this.getCustomerAddRequest())
                .done((response: Data.IResponse<CustomerData.ICustomerAddResponse>) => {
                    if (response.Success) {
                        this.viewModel.TemporaryPassword(response.Payload.TemporaryPassword);
                        this.viewModel.IsCustomerCreated(true);
                        deferred.resolve();
                    } else {
                        UI.showErrorMessage(response.ErrorMessages);
                        deferred.reject();
                    }
                })
                .fail(() => {
                    deferred.reject();
                })
                .always(() => {
                    UI.hideWaitIndicator(me.container, waitContainer);
                });
        } else {
            // not all valid fields are entered
            deferred.reject();
        }

        return deferred.promise();
    }

    private getCustomerAddRequest(): CustomerData.ICustomerAddRequest {
        const customerAddRequest: CustomerData.ICustomerAddRequest = {
            CustomerName: this.viewModel.CustomerName(),
            CustomerShortName: this.viewModel.CustomerShortName(),
            UserName: this.viewModel.UserName(),
            UserFirstName: this.viewModel.UserFirstName(),
            UserLastName: this.viewModel.UserLastName(),
            UserEmail: this.viewModel.UserEmail(),
        };

        return customerAddRequest;
    }

    private setDefaultValues() {
        this.viewModel.IsCustomerCreated(false);
        this.viewModel.TemporaryPassword("");

        const app = Global.app();
        if (!app) {
            return;
        }
        const userSession = app.userSession;
        if (!userSession) {
            return;
        }

        this.viewModel.UserFirstName(userSession.FirstName);
        this.viewModel.UserLastName(userSession.LastName);
        this.viewModel.UserEmail(userSession.UserEmail);
    }
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--7!./src/script/controllers/customerAdd.ts